import React from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle,
  LucideIcon,
  X,
  AlertCircle,
} from "lucide-react";

export enum ALERT_STATUS {
  SUCCESS = "SUCCESS",
  FAIL = "FAIL",
  WARNING = "WARNING",
}

interface AlertProps {
  title?: string;
  message?: React.ReactNode | string;

  icon: LucideIcon;
  iconColor: string;
  iconBg: string;

  onClose: () => void;
}

const Alert: React.FC<AlertProps> = ({
  title = "Alert",
  message = "",
  icon: Icon,
  iconColor,
  iconBg,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-[scaleIn_0.2s_ease-out]">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${iconBg}`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          </div>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-5 py-4 text-gray-700 text-sm sm:text-base">
          {message}
        </div>

        {/* FOOTER */}
        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            className="w-full bg-gray-700 text-white py-2.5 rounded-lg hover:bg-black transition-colors"
          >
            OK
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from { transform: scale(.9); opacity: 0 }
          to { transform: scale(1); opacity: 1 }
        }
      `}</style>
    </div>
  );
};

/* ------------------ ONE-LINE HELPER ------------------ */
export const showAlert = (
  status: ALERT_STATUS,
  title: string,
  // message: string,
  message?: React.ReactNode | string,
  options?: {
    icon?: LucideIcon;
    iconColor?: string;
    iconBg?: string;
  },
) => {
  const container = document.createElement("div");
  document.body.appendChild(container);

  const root = createRoot(container);

  const close = () => {
    root.unmount();
    container.remove();
  };

  // ✅ Status-based config
  const statusConfig: Record<
    ALERT_STATUS,
    { icon: LucideIcon; iconColor: string; iconBg: string }
  > = {
    [ALERT_STATUS.SUCCESS]: {
      icon: CheckCircle,
      iconColor: "text-green-600",
      iconBg: "bg-green-100",
    },
    [ALERT_STATUS.FAIL]: {
      icon: AlertTriangle,
      iconColor: "text-red-600",
      iconBg: "bg-red-100",
    },
    [ALERT_STATUS.WARNING]: {
      icon: AlertCircle,
      iconColor: "text-yellow-600",
      iconBg: "bg-yellow-100",
    },
  };

  const defaultConfig = statusConfig[status];

  root.render(
    <Alert
      title={title}
      message={message}
      icon={options?.icon || defaultConfig.icon}
      iconColor={options?.iconColor || defaultConfig.iconColor}
      iconBg={options?.iconBg || defaultConfig.iconBg}
      onClose={close}
    />,
  );
};
